import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { from, Observable, throwError } from 'rxjs';
import { catchError, map, mergeMap, tap } from 'rxjs/operators';
import { AppConfig } from '../app-config.loader';
import { LoaderExclusionType, LoaderService } from '../layout/loader/loader.service';
import { Posizione } from '../shared/models/posizione.model';

@Injectable({
  providedIn: 'root'
})
export class PosizioniService {
  private apiBaseUrl: string;
  private posizioni: Posizione[] = [];

  constructor(appConfig: AppConfig, private http: HttpClient) {
    this.apiBaseUrl = `${appConfig.settings.apiRoot}`;
    LoaderService.addExclusion('posizioni/level', LoaderExclusionType.isContained);
  }

  public getTree(): Observable<Posizione[]> {
    if (this.posizioni.length > 0) {
      return new Observable<Posizione[]>(observer => {
        observer.next(this.posizioni);
        observer.complete();
      });
    }

    return this.http.get<Posizione[]>(`${this.apiBaseUrl}/posizioni`).pipe(
      tap(posizioni => this.posizioni = posizioni)
    );
  }

  public getAncestor(posizioneId: string): Observable<Posizione[]> {
    const re = /\//ig;
    posizioneId = posizioneId.replace(re, '_');
    return this.http.get<Posizione[]>(this.apiBaseUrl + '/posizioni/' + posizioneId + '/ancestor');

  }

  public getPosizioni(): Observable<Posizione[]> {
    return this.http.get<Posizione[]>(this.apiBaseUrl + '/posizioni/_/children');
  }

  public downloadAggregazioneXml(posizioneId: string): Observable<void> {
    const path = posizioneId.replace(/\//g, '_');
    return this.http.get<Blob>(`${this.apiBaseUrl}/posizioni/${path}/aggregazione-xml`, {
      responseType: 'blob' as 'json',
      observe: 'response',
    }).pipe(
      tap(response => this.downloadBlobResponse(response, `fascicolo_${path}.xml`, 'application/xml')),
      map(() => void 0),
      catchError((error: HttpErrorResponse) => this.normalizeBlobError(error))
    );
  }

  getAutocompleteForPath(posizione: string, value: string, limit = 0): Observable<Posizione[]> {
    const path = posizione.replace(/\//g, '_');
    let url = `${this.apiBaseUrl}/posizioni/${path}/search`;
    if (value) {
      url += `?descrizione=${value}`;
    }

    if (limit && limit > 0) {
      url += value ? `&limit=${limit}` : `?limit=${limit}`;
    }

    LoaderService.addExclusion(`posizioni/${path}/search`, LoaderExclusionType.isContained);
    return this.http.get<Posizione[]>(url).pipe(
      tap(() => LoaderService.removeExclusion(`posizioni/${path}/search`)),
      map(posizioni => {
        posizioni.sort((a, b) => this.sortPosizioni(a, b))
        return posizioni;
      })
    );
  }

  getAutocompleteForLevel(level: number, value: string, limit = 0): Observable<Posizione[]> {
    let url = `${this.apiBaseUrl}/posizioni/level/${++level}`;
    if (value) {
      url += `?value=${value}`;
    }

    if (limit && limit > 0) {
      url += value ? `&limit=${limit}` : `?limit=${limit}`;
    }

    LoaderService.addExclusion(`posizioni/level/${level}`, LoaderExclusionType.isContained);
    return this.http.get<Posizione[]>(url).pipe(
      tap(() => LoaderService.removeExclusion(`posizioni/level/${level}`)),
      map(posizioni => {
        posizioni.sort((a, b) => this.sortPosizioni(a, b));
        return posizioni;
      })
    );
  }

  postAddFolder(folderToAdd: Posizione, father: string): Observable<Posizione> {
    const father1 = father.replace(/\//g, '_');
    return this.http.post<Posizione>(`${this.apiBaseUrl}/posizioni/${father1}`, folderToAdd).pipe(
      tap(() => this.invalidateCache())
    );
  }

  putModifyFolder(folder: Posizione): Observable<unknown> {
    const FolderToModify = { ...folder };
    FolderToModify.posizioneId = FolderToModify.posizioneId.replace(/\//g, '_');
    return this.http.put(`${this.apiBaseUrl}/posizioni/${FolderToModify.posizioneId}`, FolderToModify).pipe(
      tap(() => this.invalidateCache())
    );
  }

  searchFascicoloBy(fascicolo: string, descNotes: string): Observable<Posizione[]> {
    return this.http.get<Posizione[]>(`${this.apiBaseUrl}/posizioni/search?fascicolo=${fascicolo}&descNotes=${descNotes}`);
  }

  moveNode(from: string, to: string): Observable<unknown> {
    return this.http.post(`${this.apiBaseUrl}/posizioni/moveNode`, { from, to }).pipe(
      tap(() => this.invalidateCache())
    );
  }

  public calculateOrderString(posizione: Posizione): string {
    let expandedSigla = posizione.sigla.toLowerCase();
    if (expandedSigla.indexOf('/') === -1) {
      expandedSigla += '/';
    }

    //return expandedSigla.split('/').map(s => s.padStart(8, '0')).join('/');

    const orderSigla = expandedSigla.split('/').map(s => s.padStart(8, '0')).join('/');
    const orderDescrizione = (posizione?.descrizione)?posizione.descrizione.toLowerCase().replace(/ /g, "_"):"";
    return `${orderSigla}_${orderDescrizione}`;
  }

  public sortPosizioni(a: Posizione, b: Posizione): number {
    if (!a.orderString) {
      a.orderString = this.calculateOrderString(a);
    }

    if (!b.orderString) {
      b.orderString = this.calculateOrderString(b);
    }

    if (a.orderString > b.orderString) {
      return 1;
    } else if (a.orderString < b.orderString) {
      return -1;
    } else {
      return 0;
    }
  }

  private invalidateCache(): void {
    this.posizioni = [];
  }

  private downloadBlobResponse(response: HttpResponse<Blob>, fallbackFileName: string, contentType: string): void {
    if (!response.body) {
      return;
    }

    const header = response.headers.get('content-disposition') ?? response.headers.get('Content-Disposition');
    const fileName = this.resolveFileName(header, fallbackFileName);
    const file = new File([response.body], fileName, { type: contentType });
    const url = window.URL.createObjectURL(file);

    const link = document.createElement('a');
    link.download = fileName;
    link.href = url;
    link.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
    setTimeout(() => {
      window.URL.revokeObjectURL(url);
      link.remove();
    }, 100);
  }

  private resolveFileName(contentDisposition: string | null, fallback: string): string {
    if (!contentDisposition) {
      return fallback;
    }

    const utf8Match = /filename\\*=UTF-8''([^;]+)/i.exec(contentDisposition);
    if (utf8Match?.[1]) {
      return decodeURIComponent(utf8Match[1]);
    }

    const asciiMatch = /filename=\"?([^\";]+)\"?/i.exec(contentDisposition);
    return asciiMatch?.[1] ?? fallback;
  }

  private normalizeBlobError(error: HttpErrorResponse): Observable<never> {
    if (!(error.error instanceof Blob)) {
      return throwError(() => error);
    }

    const contentType = error.error.type || error.headers?.get('content-type') || error.headers?.get('Content-Type') || '';
    if (!contentType.includes('json') && !contentType.includes('text')) {
      return throwError(() => error);
    }

    return from(error.error.text()).pipe(
      mergeMap(rawError => {
        let parsedError: unknown = rawError;
        try {
          parsedError = JSON.parse(rawError) as unknown;
        } catch {
          parsedError = rawError;
        }

        return throwError(() => new HttpErrorResponse({
          error: parsedError,
          headers: error.headers,
          status: error.status,
          statusText: error.statusText,
          url: error.url ?? undefined,
        }));
      })
    );
  }
}
